import React, { useState, useEffect, useRef } from 'react';
import { FaBook, FaPlus, FaTrash, FaFileUpload, FaTimes, FaLock, FaUserCheck, FaSearch, FaChevronDown, FaCheck, FaEye, FaClock, FaFileContract, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as masterService from '../services/masterService';
import * as profileService from '../services/profileService';
import * as articleService from '../services/articleService';

const SubmitAbstract = () => {
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contentType, setContentType] = useState('text');
  const [hasAccess, setHasAccess] = useState(true);
  const navigate = useNavigate();

  // Master data from API
  const [masterSubjects, setMasterSubjects] = useState([]);
  const [masterStreams, setMasterStreams] = useState([]);
  const [masterSpecializations, setMasterSpecializations] = useState([]);
  const [universityList, setUniversityList] = useState([]);
  const [masterUniversities, setMasterUniversities] = useState([]);

  // Filtered data for cascading dropdowns
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [filteredSpecializations, setFilteredSpecializations] = useState([]);

  // Search states for searchable dropdowns
  const [searchDepartment, setSearchDepartment] = useState('');
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

  const [searchStream, setSearchStream] = useState('');
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);

  const [searchSpecialization, setSearchSpecialization] = useState('');
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);

  const [searchUniversity, setSearchUniversity] = useState('');
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);

  const [searchMentorUniversity, setSearchMentorUniversity] = useState('');
  const [showMentorUniversityDropdown, setShowMentorUniversityDropdown] = useState(false);

  // Reviewer multi-select states
  const [allMembers, setAllMembers] = useState({ reviewers: [], editorial_board: [], sub_eb_members: [] });
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [showReviewerDropdown, setShowReviewerDropdown] = useState(false);
  const reviewerDropdownRef = useRef(null);
  const reviewerInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    const restrictedRoles = ['graduated', 'post graduate', 'admin'];
    if (restrictedRoles.includes(currentUser.role?.toLowerCase())) {
      setHasAccess(false);
    }

    // Fetch master data
    const loadMasterData = async () => {
      try {
        const safeGetProfile = async () => {
          try {
            return await profileService.getProfile();
          } catch (e) {
            console.error('Profile fetch failed:', e);
            return null;
          }
        };

        const [subjectsRes, streamsRes, specsRes, univRes, profileData, membersRes] = await Promise.all([
          masterService.getAllSubjects(),
          masterService.getAllStreams(),
          masterService.getAllSpecializations(),
          masterService.getAllUniversities(),
          safeGetProfile(),
          masterService.getMembers().catch(e => { console.error('Members fetch failed:', e); return null; })
        ]);

        const subjectsArray = Array.isArray(subjectsRes) ? subjectsRes : (subjectsRes?.data || subjectsRes?.subjects || []);
        const streamsArray = Array.isArray(streamsRes) ? streamsRes : (streamsRes?.data || streamsRes?.streams || []);
        const specsArray = Array.isArray(specsRes) ? specsRes : (specsRes?.data || specsRes?.specializations || []);

        let univArray = [];
        if (univRes.success && Array.isArray(univRes.data)) {
          univArray = univRes.data;
        } else if (Array.isArray(univRes)) {
          univArray = univRes;
        }

        setMasterSubjects(subjectsArray);
        setMasterStreams(streamsArray);
        setMasterSpecializations(specsArray);
        const topUnivs = univArray.slice(0, 50);
        setUniversityList(topUnivs);
        setMasterUniversities(topUnivs);

        // Auto-fill university from profile if available
        if (profileData?.success && profileData?.data?.academic?.university) {
          const userUniversity = profileData.data.academic.university;
          setFormData(prev => ({
            ...prev,
            university: userUniversity
          }));
          setSearchUniversity(userUniversity);
        }

        if (membersRes) {
          const data = membersRes.data || membersRes;
          setAllMembers({
            reviewers: Array.isArray(data.reviewers) ? data.reviewers : [],
            editorial_board: Array.isArray(data.editorial_board || data.editorialBoard)
              ? (data.editorial_board || data.editorialBoard) : [],
            sub_eb_members: Array.isArray(data.sub_eb_members || data.subEB)
              ? (data.sub_eb_members || data.subEB) : []
          });
        }
      } catch (error) {
        console.error('Error loading master data:', error);
      }
    };

    loadMasterData();
  }, [navigate]);

  // Close reviewer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        reviewerDropdownRef.current &&
        !reviewerDropdownRef.current.contains(e.target)
      ) {
        setShowReviewerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced university search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchUniversity && searchUniversity.length >= 2) {
        if (formData.university === searchUniversity) return;
        try {
          const response = await masterService.searchUniversities(searchUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      } else {
        setUniversityList(masterUniversities);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUniversity]);

  // Debounced mentor university search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchMentorUniversity && searchMentorUniversity.length >= 2) {
        if (formData.mentorUniversity === searchMentorUniversity) return;
        try {
          const response = await masterService.searchUniversities(searchMentorUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowMentorUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      } else {
        setUniversityList(masterUniversities);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMentorUniversity]);

  const [formData, setFormData] = useState({
    department: '',
    departmentName: '',
    stream: '',
    streamName: '',
    specialization: '',
    specializationName: '',
    university: '',
    abstractTitle: '',
    mentorName: '',
    mentorDesignation: '',
    mentorUniversity: '',
    mentorPhone: '',
    mentorEmail: '',
    submissionDate: new Date().toISOString().split('T')[0],
    abstractText: '',
    keywords: '',
  });

  const [authors, setAuthors] = useState([
    {
      id: 1,
      name: '',
      email: '',
      university: '',
      searchUniversity: '',
      showUniversityDropdown: false,
    },
  ]);

  const [uploadedFile, setUploadedFile] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAuthorChange = (id, field, value) => {
    setAuthors(prevAuthors =>
      prevAuthors.map((author) =>
        author.id === id ? { ...author, [field]: value } : author
      )
    );

    // For authors, the global useEffects don't track individual searchUniversity fields
    // but the shared universityList is used. We filter it in the render.
    if (field === 'searchUniversity') {
      if (value.length < 2) {
        setUniversityList(masterUniversities);
      } else {
        // Trigger a search that updates the shared universityList
        const fetchUnivs = async () => {
          try {
            const response = await masterService.searchUniversities(value);
            if (response.success && Array.isArray(response.data)) {
              setUniversityList(response.data);
            } else if (Array.isArray(response)) {
              setUniversityList(response);
            }
          } catch (err) {
            console.error('Author university search failed:', err);
          }
        };
        fetchUnivs();
      }
    }
  };

  const addAuthor = () => {
    const newId = Math.max(...authors.map((a) => a.id)) + 1;
    setAuthors([
      ...authors,
      {
        id: newId,
        name: '',
        email: '',
        university: '',
        searchUniversity: '',
        showUniversityDropdown: false,
      },
    ]);
  };

  const removeAuthor = (id) => {
    if (authors.length > 1) {
      setAuthors(authors.filter((author) => author.id !== id));
    } else {
      showToast('At least one author is required', 'error');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setUploadedFile(file);
        setContentType('file');
        setFormData({ ...formData, abstractText: '' });
      } else {
        showToast('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setContentType('text');
  };

  const handleAbstractTextChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, abstractText: value });

    if (value.trim()) {
      setContentType('text');
      setUploadedFile(null);
    }
  };

  const validateForm = () => {
    // Collect effective values (taking typed search values if dropdown wasn't selected)
    const effectiveUniversity = formData.university || searchUniversity;
    const effectiveDept = formData.department || searchDepartment;
    const effectiveStream = formData.stream || searchStream;

    if (!effectiveDept || !effectiveStream || !formData.abstractTitle || !effectiveUniversity) {
      showToast('Please fill in all required fields', 'error');
      return false;
    }

    // Authors are optional as per user request

    if (!formData.abstractText && !uploadedFile) {
      showToast('Please provide abstract content or upload a document', 'error');
      return false;
    }

    return true;
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();

    if (validateForm()) {
      submitAbstract();
    }
  };
  const submitAbstract = async () => {
    setIsSubmitting(true);

    try {
      // 1. Gather Effective Values
      const finalUniversity = formData.university || searchUniversity;
      const finalMentorUniversity = formData.mentorUniversity || searchMentorUniversity;

      // Ensure we have IDs for required fields (UUIDs are expected by the backend)
      if (!formData.department || !formData.stream) {
        showToast('Please select Department and Stream from the suggested list', 'error');
        setIsSubmitting(false);
        return;
      }

      // Filter co-authors and clean fields
      const coAuthors = authors
        .filter(a => a.name || a.email || (a.university || a.searchUniversity))
        .map(a => ({
          name: a.name || 'Author',
          email: a.email || '',
          university: a.university || a.searchUniversity || ''
        }));

      // Group Mentor/Guide Info into guide_details object
      const guideDetails = {
        name: formData.mentorName,
        designation: formData.mentorDesignation,
        university: finalMentorUniversity,
        phone: formData.mentorPhone,
        email: formData.mentorEmail
      };

      const reviewerIds = selectedReviewers.map(r => r.id);

      let submissionData;
      if (uploadedFile) {
        // Use FormData for file uploads
        submissionData = new FormData();
        submissionData.append('title', formData.abstractTitle);
        submissionData.append('subject_id', formData.department);
        submissionData.append('stream_id', formData.stream);
        if (formData.specialization) submissionData.append('specialization_id', formData.specialization);
        submissionData.append('abstract_text', formData.abstractText || '');
        submissionData.append('keywords', formData.keywords);
        submissionData.append('submission_date', formData.submissionDate);
        submissionData.append('university', finalUniversity);
        submissionData.append('guide_details', JSON.stringify(guideDetails));
        submissionData.append('co_authors', JSON.stringify(coAuthors));
        submissionData.append('suggested_reviewer_ids', JSON.stringify(reviewerIds));
        submissionData.append('file', uploadedFile);
        submissionData.append('contentType', 'file'); // Indicate file upload type
      } else {
        // Use plain object for JSON submission (no file)
        submissionData = {
          title: formData.abstractTitle,
          subject_id: formData.department,
          stream_id: formData.stream,
          specialization_id: formData.specialization || null, // Send null if not selected
          abstract_text: formData.abstractText || '',
          keywords: formData.keywords,
          submission_date: formData.submissionDate,
          university: finalUniversity,
          guide_details: guideDetails,
          co_authors: coAuthors,
          suggested_reviewer_ids: reviewerIds
        };
      }

      console.log('Submitting abstract to API...', submissionData instanceof FormData ? 'Multipart/Form-Data' : 'JSON');
      // Log FormData content for debugging if it's FormData
      if (submissionData instanceof FormData) {
        const debugData = {};
        for (var pair of submissionData.entries()) {
          debugData[pair[0]] = pair[1];
        }
        console.log('FormData Payload:', debugData);
      } else {
        console.log('JSON Payload:', submissionData);
      }

      const response = await articleService.submitAbstract(submissionData);

      console.log('Submission response:', response);

      showToast('Abstract submitted successfully!', 'success');

      setTimeout(() => {
        // Reset form and related states
        setFormData({
          department: '',
          departmentName: '',
          stream: '',
          streamName: '',
          specialization: '',
          specializationName: '',
          university: '',
          abstractTitle: '',
          mentorName: '',
          mentorDesignation: '',
          mentorUniversity: '',
          mentorPhone: '',
          mentorEmail: '',
          submissionDate: new Date().toISOString().split('T')[0],
          abstractText: '',
          keywords: '',
        });
        setAuthors([{ id: 1, name: '', email: '', university: '', searchUniversity: '', showUniversityDropdown: false }]);
        setUploadedFile(null);
        setContentType('text');
        setSearchDepartment('');
        setSearchStream('');
        setSearchSpecialization('');
        setSearchUniversity('');
        setSearchMentorUniversity('');
        setFilteredStreams([]);
        setFilteredSpecializations([]);
        setSelectedReviewers([]);
        setReviewerSearch('');

        // ✅ Add this line to clear the actual file input element
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsSubmitting(false);
      }, 2000);

    } catch (error) {
      console.error('Error submitting abstract:', error);
      // Improved error message display
      const errorMessage = error.response?.data?.message || error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      showToast('Submission error: ' + errorMessage, 'error');
      setIsSubmitting(false);
    }
  };

  // Department selection handler
  const handleDepartmentSelect = (sub) => {
    setFormData({
      ...formData,
      department: sub.id,
      departmentName: sub.name,
      stream: '',
      streamName: '',
      specialization: '',
      specializationName: '',
    });
    setSearchDepartment(sub.name);
    setSearchStream('');
    setSearchSpecialization('');
    const filtered = masterStreams.filter(m => String(m.subject_id) === String(sub.id));
    setFilteredStreams(filtered);
    setFilteredSpecializations([]);
    setShowDepartmentDropdown(false);
  };

  // Stream selection handler
  const handleStreamSelect = (str) => {
    setFormData({
      ...formData,
      stream: str.id,
      streamName: str.name,
      specialization: '',
      specializationName: '',
    });
    setSearchStream(str.name);
    setSearchSpecialization('');
    const filtered = masterSpecializations.filter(s => String(s.stream_id) === String(str.id));
    setFilteredSpecializations(filtered);
    setShowStreamDropdown(false);
  };

  // Specialization selection handler
  const handleSpecializationSelect = (spec) => {
    setFormData({ ...formData, specialization: spec.id, specializationName: spec.name });
    setSearchSpecialization(spec.name);
    setShowSpecializationDropdown(false);
  };

  // ─── Reviewer helpers ────────────────────────────────────────────────────────

  const isReviewerSelected = (id) => selectedReviewers.some(r => r.id === id);

  const toggleReviewer = (item, type) => {
    const name = item.full_name || item.name || 'Unnamed Member';
    if (isReviewerSelected(item.id)) {
      // Deselect
      setSelectedReviewers(prev => prev.filter(r => r.id !== item.id));
    } else {
      // Select — keep dropdown open for multi-pick
      setSelectedReviewers(prev => [...prev, { ...item, type, full_name: name }]);
    }
    // Keep input focused and dropdown open
    reviewerInputRef.current?.focus();
  };

  const handleReviewerInputClick = () => {
    setShowReviewerDropdown(prev => !prev);
  };

  // ─────────────────────────────────────────────────────────────────────────────

  if (!hasAccess) {
    return (
      <>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6 flex items-center justify-center">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaLock className="text-5xl text-red-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Access Restricted</h2>
              <p className="text-lg text-gray-600 mb-6">
                Abstract submission is not available for your user role. Only undergraduate students and researchers can submit abstracts.
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }



  const statusColors = {
    submitted: 'text-blue-600 bg-blue-50 border-blue-100',
    in_review: 'text-yellow-600 bg-yellow-50 border-yellow-100',
    accepted: 'text-green-600 bg-green-50 border-green-100',
    rejected: 'text-red-600 bg-red-50 border-red-100'
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";
  const sectionBadgeClass = "w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold shadow-lg text-white";

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          <div className="rounded-2xl shadow-lg p-6 mb-6 relative overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Submit Abstract for Review</h1>
                <p className="text-blue-100 text-sm">Share your research abstract with the community</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaBook className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>



          <div className="bg-white rounded-2xl shadow-lg">
            <form onSubmit={handleProceedToPayment}>

              {/* Section 1: Academic Information */}
              <div className="p-6 border-b-2 border-gray-100 relative z-70 rounded-t-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>1</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Academic Information</h2>
                    <p className="text-gray-500 text-sm">Select your field of study</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Department (Subject) - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>Department <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Search Department"
                      value={searchDepartment}
                      onChange={(e) => {
                        setSearchDepartment(e.target.value);
                        setShowDepartmentDropdown(true);
                      }}
                      onFocus={() => setShowDepartmentDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDepartmentDropdown(false), 200)}
                      className={`${inputClass} bg-white`}
                      required={!formData.department}
                    />
                    {formData.department && (
                      <input type="hidden" name="department" value={formData.department} />
                    )}
                    {showDepartmentDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {masterSubjects.filter(u => u.name.toLowerCase().includes(searchDepartment.toLowerCase())).map((sub) => (
                          <div
                            key={sub.id}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                            onMouseDown={() => handleDepartmentSelect(sub)}
                          >
                            {sub.name}
                          </div>
                        ))}
                        {masterSubjects.filter(u => u.name.toLowerCase().includes(searchDepartment.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-gray-400 text-sm">No results</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Stream - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>Stream <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Search Stream"
                      value={searchStream}
                      onChange={(e) => {
                        setSearchStream(e.target.value);
                        setShowStreamDropdown(true);
                      }}
                      onFocus={() => setShowStreamDropdown(true)}
                      onBlur={() => setTimeout(() => setShowStreamDropdown(false), 200)}
                      disabled={!formData.department}
                      className={`${inputClass} bg-white disabled:bg-gray-50`}
                      required={!formData.stream}
                    />
                    {showStreamDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {filteredStreams.filter(u => u.name.toLowerCase().includes(searchStream.toLowerCase())).map((str) => (
                          <div
                            key={str.id}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                            onMouseDown={() => handleStreamSelect(str)}
                          >
                            {str.name}
                          </div>
                        ))}
                        {filteredStreams.filter(u => u.name.toLowerCase().includes(searchStream.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-gray-400 text-sm">{!formData.department ? 'Select Department first' : 'No results'}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Specialization - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>Specialization</label>
                    <input
                      type="text"
                      placeholder="Search Specialization"
                      value={searchSpecialization}
                      onChange={(e) => {
                        setSearchSpecialization(e.target.value);
                        setShowSpecializationDropdown(true);
                      }}
                      onFocus={() => setShowSpecializationDropdown(true)}
                      onBlur={() => setTimeout(() => setShowSpecializationDropdown(false), 200)}
                      disabled={!formData.stream}
                      className={`${inputClass} bg-white disabled:bg-gray-50`}
                    />
                    {showSpecializationDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {filteredSpecializations.filter(u => u.name.toLowerCase().includes(searchSpecialization.toLowerCase())).map((spec) => (
                          <div
                            key={spec.id}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                            onMouseDown={() => handleSpecializationSelect(spec)}
                          >
                            {spec.name}
                          </div>
                        ))}
                        {filteredSpecializations.filter(u => u.name.toLowerCase().includes(searchSpecialization.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-gray-400 text-sm">{!formData.stream ? 'Select Stream first' : 'No results'}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* University - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>University <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Search & Select University"
                      value={formData.university || searchUniversity}
                      onChange={(e) => {
                        setSearchUniversity(e.target.value);
                        setFormData({ ...formData, university: '' });
                        setShowUniversityDropdown(true);
                      }}
                      onFocus={() => setShowUniversityDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUniversityDropdown(false), 200)}
                      className={`${inputClass} bg-white`}
                      required
                    />
                    {showUniversityDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {universityList.length > 0 ? (
                          universityList.map((uni, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              onMouseDown={() => {
                                const name = uni.name || uni;
                                setFormData({ ...formData, university: name });
                                setSearchUniversity(name);
                                setShowUniversityDropdown(false);
                              }}
                            >
                              {uni.name || uni}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                            onMouseDown={() => {
                              setFormData({ ...formData, university: 'Other' });
                              setSearchUniversity('Other');
                              setShowUniversityDropdown(false);
                            }}
                          >
                            Other (Type manually)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Abstract Details */}
              <div className="p-6 bg-gradient-to-br from-purple-50/30 to-pink-50/30 border-b-2 border-gray-100 relative z-60">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>2</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Abstract Details</h2>
                    <p className="text-gray-500 text-sm">Provide information about your abstract</p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Abstract Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="abstractTitle"
                    value={formData.abstractTitle}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="Enter the title of your research abstract"
                  />
                </div>

                <div>
                  <label className={labelClass}>Submission Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="submissionDate"
                    value={formData.submissionDate}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Section 3: Authors Information */}
              <div className="p-6 border-b-2 border-gray-100 relative z-60">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>3</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Authors Information <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-normal">Optional</span></h2>
                    <p className="text-gray-500 text-sm">Add all contributing authors</p>
                  </div>
                </div>

                {authors.map((author, index) => (
                  <div key={author.id} className="mb-4 p-4 bg-green-50/50 rounded-xl border-l-4 border-green-500">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <div className="w-7 h-7 bg-green-500 text-white rounded-lg flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </div>
                        Author {index + 1}
                      </h3>
                      {authors.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAuthor(author.id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={labelClass}>Author Name</label>
                        <input
                          type="text"
                          value={author.name}
                          onChange={(e) => handleAuthorChange(author.id, 'name', e.target.value)}
                          className={inputClass}
                          placeholder="Full name"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Author Email</label>
                        <input
                          type="email"
                          value={author.email}
                          onChange={(e) => handleAuthorChange(author.id, 'email', e.target.value)}
                          className={inputClass}
                          placeholder="email@example.com"
                        />
                      </div>

                      <div className="relative">
                        <label className={labelClass}>Author University</label>
                        <input
                          type="text"
                          placeholder="Search & Select University"
                          value={author.university || author.searchUniversity || ''}
                          onChange={(e) => {
                            handleAuthorChange(author.id, 'searchUniversity', e.target.value);
                            handleAuthorChange(author.id, 'university', '');
                            handleAuthorChange(author.id, 'showUniversityDropdown', true);
                          }}
                          onFocus={() => handleAuthorChange(author.id, 'showUniversityDropdown', true)}
                          onBlur={() => setTimeout(() => handleAuthorChange(author.id, 'showUniversityDropdown', false), 200)}
                          className={`${inputClass} bg-white`}
                        />
                        {author.showUniversityDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {universityList.filter(u => {
                              const name = (u.name || u || '').toLowerCase();
                              const search = (author.searchUniversity || '').toLowerCase();
                              return !search || name.includes(search);
                            }).length > 0 ? (
                              universityList.filter(u => {
                                const name = (u.name || u || '').toLowerCase();
                                const search = (author.searchUniversity || '').toLowerCase();
                                return !search || name.includes(search);
                              }).map((uni, idx) => (
                                <div
                                  key={idx}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    const name = uni.name || uni;
                                    handleAuthorChange(author.id, 'university', name);
                                    handleAuthorChange(author.id, 'searchUniversity', name);
                                    handleAuthorChange(author.id, 'showUniversityDropdown', false);
                                  }}
                                >
                                  {uni.name || uni}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                                onMouseDown={() => {
                                  handleAuthorChange(author.id, 'university', 'Other');
                                  handleAuthorChange(author.id, 'searchUniversity', 'Other');
                                  handleAuthorChange(author.id, 'showUniversityDropdown', false);
                                }}
                              >
                                Other (Type manually)
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addAuthor}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold"
                >
                  <FaPlus />
                  Add Another Author
                </button>
              </div>

              {/* Section 4: Mentor Information */}
              <div className="p-6 bg-gradient-to-br from-orange-50/30 to-yellow-50/30 border-b-2 border-gray-100 relative z-40">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>4</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Mentor Information <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-normal">Optional</span></h2>
                    <p className="text-gray-500 text-sm">Research mentor details</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className={labelClass}>Mentor Name</label>
                    <input
                      type="text"
                      name="mentorName"
                      value={formData.mentorName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Mentor's full name"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Designation</label>
                    <input
                      type="text"
                      name="mentorDesignation"
                      value={formData.mentorDesignation}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g., Professor"
                    />
                  </div>

                  {/* Mentor University - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>Mentor University</label>
                    <input
                      type="text"
                      placeholder="Search & Select University"
                      value={formData.mentorUniversity || searchMentorUniversity}
                      onChange={(e) => {
                        setSearchMentorUniversity(e.target.value);
                        setFormData({ ...formData, mentorUniversity: '' });
                        setShowMentorUniversityDropdown(true);
                      }}
                      onFocus={() => setShowMentorUniversityDropdown(true)}
                      onBlur={() => setTimeout(() => setShowMentorUniversityDropdown(false), 200)}
                      className={`${inputClass} bg-white`}
                    />
                    {showMentorUniversityDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {universityList.length > 0 ? (
                          universityList.map((uni, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              onMouseDown={() => {
                                const name = uni.name || uni;
                                setFormData({ ...formData, mentorUniversity: name });
                                setSearchMentorUniversity(name);
                                setShowMentorUniversityDropdown(false);
                              }}
                            >
                              {uni.name || uni}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                            onMouseDown={() => {
                              setFormData({ ...formData, mentorUniversity: 'Other' });
                              setSearchMentorUniversity('Other');
                              setShowMentorUniversityDropdown(false);
                            }}
                          >
                            Other (Type manually)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass}>Mentor Phone</label>
                    <input
                      type="tel"
                      name="mentorPhone"
                      value={formData.mentorPhone}
                      onChange={handleChange}
                      maxLength="10"
                      className={inputClass}
                      placeholder="10-digit number"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Mentor Email</label>
                    <input
                      type="email"
                      name="mentorEmail"
                      value={formData.mentorEmail}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="mentor@university.edu"
                    />
                  </div>
                </div>
              </div>

              {/* Section 5: Select Reviewers */}
              <div className="p-6 bg-gradient-to-br from-indigo-50/30 to-blue-50/30 border-b-2 border-gray-100 relative z-30">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>5</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Select Reviewers{' '}
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-normal">Optional</span>
                    </h2>
                    <p className="text-gray-500 text-sm">Choose one or more reviewers — tick multiple from the list</p>
                  </div>
                </div>

                {/* Selected Reviewers Chips */}
                {selectedReviewers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedReviewers.map((reviewer) => (
                      <div
                        key={reviewer.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-800 rounded-full text-sm font-medium border border-indigo-200 shadow-sm"
                      >
                        <FaUserCheck className="text-indigo-600 text-xs" />
                        <span>{reviewer.full_name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedReviewers(prev => prev.filter(r => r.id !== reviewer.id))}
                          className="ml-1 w-4 h-4 flex items-center justify-center bg-indigo-300 hover:bg-red-400 text-white rounded-full transition-colors"
                        >
                          <FaTimes className="text-[8px]" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSelectedReviewers([])}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 rounded-full text-xs font-medium border border-red-200 hover:bg-red-100 transition-colors"
                    >
                      <FaTimes className="text-[8px]" /> Clear all
                    </button>
                  </div>
                )}

                {/* Reviewer Dropdown — controlled via click toggle + outside click */}
                <div className="relative" ref={reviewerDropdownRef}>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                    <input
                      ref={reviewerInputRef}
                      type="text"
                      placeholder="Search reviewers by name or click to browse…"
                      value={reviewerSearch}
                      onChange={(e) => {
                        setReviewerSearch(e.target.value);
                        setShowReviewerDropdown(true);
                      }}
                      onClick={handleReviewerInputClick}
                      className={`${inputClass} pl-10 pr-10 bg-white cursor-pointer`}
                      // No onBlur here — we use the outside-click listener instead
                      readOnly={false}
                    />
                    <FaChevronDown
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm transition-transform duration-200 pointer-events-none ${showReviewerDropdown ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {showReviewerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto">
                      {(() => {
                        const search = (reviewerSearch || '').toLowerCase();

                        const isMatch = (item) => {
                          const name = (item.full_name || item.name || '').toLowerCase();
                          return search === '' || name.includes(search);
                        };

                        // Flatten all members into a single list
                        const allDisplayMembers = [
                          ...allMembers.reviewers.map(m => ({ ...m, roleLabel: 'Reviewer', type: 'reviewer' })),
                          ...allMembers.editorial_board.map(m => ({ ...m, roleLabel: 'EB Member', type: 'editorial_board' })),
                          ...allMembers.sub_eb_members.map(m => ({ ...m, roleLabel: 'Sub-EB Member', type: 'sub_eb' }))
                        ].filter((item, index, self) =>
                          // Remove duplicates by ID and match search
                          index === self.findIndex((t) => t.id === item.id) && isMatch(item)
                        );

                        if (allDisplayMembers.length === 0) {
                          return (
                            <div className="px-4 py-8 text-center bg-white">
                              <p className="text-gray-400 text-sm">
                                {reviewerSearch ? `No results for "${reviewerSearch}"` : 'No reviewers available'}
                              </p>
                            </div>
                          );
                        }

                        // Use a consistent theme color for all avatars
                        const commonColor = 'bg-indigo-100 text-indigo-700';

                        return (
                          <div className="py-1 bg-white">
                            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/50">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Available Reviewers</p>
                            </div>
                            {allDisplayMembers.map((item) => {
                              const name = item.full_name || item.name || 'Unnamed Member';
                              const selected = isReviewerSelected(item.id);
                              return (
                                <div
                                  key={item.id}
                                  className={`px-4 py-2.5 cursor-pointer transition-colors flex items-center gap-3 border-b border-gray-50 last:border-0 ${selected ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'
                                    }`}
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // prevent input blur
                                    toggleReviewer(item, item.type);
                                  }}
                                >
                                  {/* Checkbox indicator */}
                                  <div
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected
                                      ? 'bg-indigo-600 border-indigo-600'
                                      : 'border-gray-300 bg-white'
                                      }`}
                                  >
                                    {selected && <FaCheck className="text-white text-[9px]" />}
                                  </div>
                                  {/* Avatar - now uses unified color */}
                                  <div className={`w-8 h-8 ${commonColor} rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs`}>
                                    {name.charAt(0).toUpperCase()}
                                  </div>
                                  {/* Name + university */}
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold truncate ${selected ? 'text-indigo-700' : 'text-gray-800'}`}>
                                      {name}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter shadow-sm border ${item.type === 'editorial_board'
                                        ? 'bg-amber-100 text-amber-700 border-amber-200'
                                        : item.type === 'sub_eb'
                                          ? 'bg-purple-100 text-purple-700 border-purple-200'
                                          : 'bg-blue-100 text-blue-700 border-blue-200'
                                        }`}>
                                        {item.roleLabel}
                                      </span>
                                      {item.university && (
                                        <p className="text-[10px] text-gray-500 truncate">{item.university}</p>
                                      )}
                                    </div>
                                  </div>
                                  {selected && (
                                    <span className="text-xs text-indigo-500 font-medium flex-shrink-0">Selected</span>
                                  )}
                                </div>
                              );
                            })}

                            {/* Footer with done button */}
                            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                              <span className="text-xs text-indigo-600 font-semibold">
                                {selectedReviewers.length} reviewer{selectedReviewers.length !== 1 ? 's' : ''} selected
                              </span>
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setShowReviewerDropdown(false);
                                  setReviewerSearch('');
                                }}
                                className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {selectedReviewers.length > 0 && !showReviewerDropdown && (
                  <p className="text-xs text-indigo-600 mt-2 font-medium">
                    {selectedReviewers.length} reviewer{selectedReviewers.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Section 6: Abstract Content */}
              <div className="p-6 border-b-2 border-gray-100 relative z-20">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>6</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Abstract Content <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">MANDATORY</span></h2>
                    <p className="text-gray-500 text-sm">Write your abstract or upload a document</p>
                  </div>
                </div>

                {!uploadedFile && (
                  <div className="mb-6">
                    <label className={labelClass}>Abstract Text <span className="text-red-500">*</span></label>
                    <textarea
                      name="abstractText"
                      value={formData.abstractText}
                      onChange={handleAbstractTextChange}
                      rows="12"
                      className={inputClass}
                      placeholder="Type or paste your abstract content here..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">Or upload a document below</p>
                  </div>
                )}

                {!formData.abstractText && (
                  <div className="mb-6">
                    <label className={labelClass}>Upload Document (PDF or Word)</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        ref={fileInputRef}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100 cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, DOC, DOCX</p>
                  </div>
                )}

                {uploadedFile && (
                  <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                          <FaFileUpload className="text-white text-xl" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            {(uploadedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                        title="Remove file"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Keywords <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-normal">Optional</span></label>
                  <input
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g., machine learning, artificial intelligence, data science"
                  />
                  <p className="text-xs text-gray-500 mt-2">Separate keywords with commas</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="p-6 flex justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-b-2xl relative z-10">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex items-center gap-3 px-12 py-4 bg-gradient-to-r from-primary-blue via-blue-600 to-secondary-blue text-white text-lg font-bold rounded-xl hover:shadow-xl transform hover:scale-105 transition-all shadow-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed transform-none' : ''
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaBook />
                      Submit Abstract
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitAbstract;